import { useMutation } from '@apollo/client/react'
import { zodResolver } from '@hookform/resolvers/zod'
import { Loader } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Navigate, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { z } from 'zod'
import { BrandLogo } from '~/components/BrandLogo'
import { Button } from '~/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '~/components/ui/card'
import { Form, FormControl, FormField, FormItem, FormMessage } from '~/components/ui/form'
import { Input } from '~/components/ui/input'
import { Label } from '~/components/ui/label'
import { GQL_FINISH_REGISTER } from '~/gql/auth/auth'

/**
 * Finalisation d'un compte pré-inscrit par un admin d'org (#134).
 *
 * Cette page n'ouvre PLUS d'inscription libre : sans `?token=` elle n'a rien à
 * finaliser et renvoie vers le login. Le compte doit déjà exister côté API, la
 * mutation `finishRegister` ne fait que lui poser un mot de passe.
 */
function CreateAccount() {
  const navigate = useNavigate()
  const { t } = useTranslation()

  const queryString = window.location.search
  const urlParams = new URLSearchParams(queryString)
  const token = urlParams.get('token')

  const createAccountSchema = z.object({
    firstName: z.string().min(1, { message: t('input.required.message') }),
    lastName: z.string().min(1, { message: t('input.required.message') }),
    email: z.string().email({ message: t('input.email.message') }),
    password: z.string().min(1, { message: t('input.password') }),
    confirmPassword: z.string().min(1, { message: t('input.password-confirm') }),
  }).refine(data => data.password === data.confirmPassword, {
    message: t('password.not-match.message'),
    path: ['confirmPassword'],
  })

  type CreateAccountFormValues = z.infer<typeof createAccountSchema>

  const form = useForm<CreateAccountFormValues>({
    resolver: zodResolver(createAccountSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: '',
    },
  })

  const [finishRegister, { loading }] = useMutation(GQL_FINISH_REGISTER)

  const onSubmit = async (values: CreateAccountFormValues) => {
    if (!token) return

    try {
      const { data } = await finishRegister({
        variables: {
          email: values.email,
          firstName: values.firstName,
          lastName: values.lastName,
          password: values.password,
          token,
        },
      })
      if (data && data.finishRegister) {
        toast.success(t('good.register.message'))
        navigate('/')
      } else {
        toast.error(t('bad.register.message'))
      }
    } catch {
      toast.error(t('bad.register.message'))
    }
  }

  if (!token) return <Navigate to="/login" replace />

  return (
    <div className="flex w-screen min-h-dvh flex-col items-center justify-center p-5">
      <Card className="sm:w-xl w-full">
        <CardHeader className="gap-4">
          <BrandLogo className="h-9 w-auto" fallbackClassName="text-lg" />
          <CardTitle className="font-heading text-2xl tracking-tight">{t('finish.register')}</CardTitle>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-6">
              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem className="w-full mx-auto">
                    <FormControl>
                      <div className="grid gap-2">
                        <Label htmlFor="firstName">{t('common.firstname')}</Label>
                        <Input
                          id="firstName"
                          type="text"
                          {...field}
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem className="w-full mx-auto">
                    <FormControl>
                      <div className="grid gap-2">
                        <Label htmlFor="lastName">{t('common.lastname')}</Label>
                        <Input
                          id="lastName"
                          type="text"
                          {...field}
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem className="w-full mx-auto">
                    <FormControl>
                      <div className="grid gap-2">
                        <Label htmlFor="email">{t('common.email')}</Label>
                        <Input
                          id="email"
                          type="email"
                          placeholder="m@example.com"
                          {...field}
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem className="w-full mx-auto">
                    <FormControl>
                      <div className="grid gap-2">
                        <Label htmlFor="password">{t('common.password')}</Label>
                        <Input
                          id="password"
                          type="password"
                          {...field}
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem className="w-full mx-auto">
                    <FormControl>
                      <div className="grid gap-2">
                        <Label htmlFor="confirmPassword">{t('password.confirm')}</Label>
                        <Input
                          id="confirmPassword"
                          type="password"
                          {...field}
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button type="submit" disabled={loading} className="w-full">
                {loading && <Loader className="mr-2 size-4 animate-spin" />}
                {t('common.register')}
              </Button>

              <div className="mt-4 text-center text-sm">
                <a
                  onClick={() => navigate('/')}
                  className="underline underline-offset-4 cursor-pointer"
                >
                  {t('common.login')}
                </a>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  )
}

export default CreateAccount
